import React from 'react';

export const CalculatorSelectionGuide = () => {
  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Calculator Selection Guide</h1>
        <p className="text-xl text-gray-600 mb-8">Choose the Right Calculator for Your Situation</p>

        {/* Quick Decision Tree */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">🎯 Quick Decision Tree</h2>
          
          <div className="space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <p className="font-semibold text-gray-900 mb-2">Are you still working?</p>
              <p className="text-gray-700"><span className="font-bold text-green-600">YES</span> → Start with <span className="font-bold text-blue-600">Accumulation Calculator</span></p>
              <p className="text-gray-700"><span className="font-bold text-red-600">NO</span> → Continue below ↓</p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <p className="font-semibold text-gray-900 mb-2">Are you retired or within 5 years of retirement?</p>
              <p className="text-gray-700"><span className="font-bold text-green-600">YES</span> → Continue below ↓</p>
              <p className="text-gray-700"><span className="font-bold text-red-600">NO</span> → Use <span className="font-bold text-blue-600">Accumulation Calculator</span> first</p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <p className="font-semibold text-gray-900 mb-2">Do you want a quick check or detailed analysis?</p>
              <p className="text-gray-700"><span className="font-bold text-green-600">QUICK CHECK</span> → Use <span className="font-bold text-blue-600">Basic Retirement Calculator</span></p>
              <p className="text-gray-700"><span className="font-bold text-purple-600">DETAILED ANALYSIS</span> → Use <span className="font-bold text-purple-600">Advanced Retirement Calculator</span></p>
            </div>

            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <p className="font-semibold text-gray-900 mb-2">Do you have an SMSF or considering one?</p>
              <p className="text-gray-700"><span className="font-bold text-green-600">YES</span> → Explore <span className="font-bold text-sky-600">SMSF Calculator Suite</span></p>
              <p className="text-gray-700"><span className="font-bold text-red-600">NO</span> → Focus on retirement calculators above</p>
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">📊 Calculator Comparison</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Feature</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Accumulation
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-green-100 text-green-800 border border-green-200">FREE</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Basic Retirement
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-green-100 text-green-800 border border-green-200">FREE</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Advanced Retirement
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-blue-600 to-purple-600 text-white">PRO</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    SMSF Suite
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-blue-600 to-purple-600 text-white">PRO</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Best For</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Still working</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Quick check</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Detailed planning</td>
                  <td className="px-4 py-3 text-sm text-gray-700">SMSF members</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Time Required</td>
                  <td className="px-4 py-3 text-sm text-gray-700">2-3 min</td>
                  <td className="px-4 py-3 text-sm text-gray-700">3-5 min</td>
                  <td className="px-4 py-3 text-sm text-gray-700">10-15 min</td>
                  <td className="px-4 py-3 text-sm text-gray-700">15-30 min</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Complexity</td>
                  <td className="px-4 py-3 text-sm text-gray-700">⭐ Simple</td>
                  <td className="px-4 py-3 text-sm text-gray-700">⭐⭐ Easy</td>
                  <td className="px-4 py-3 text-sm text-gray-700">⭐⭐⭐⭐ Advanced</td>
                  <td className="px-4 py-3 text-sm text-gray-700">⭐⭐⭐⭐⭐ Expert</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Age Range</td>
                  <td className="px-4 py-3 text-sm text-gray-700">18-66</td>
                  <td className="px-4 py-3 text-sm text-gray-700">55+</td>
                  <td className="px-4 py-3 text-sm text-gray-700">55+</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Any age</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Withdrawal Strategies</td>
                  <td className="px-4 py-3 text-sm text-gray-700">N/A</td>
                  <td className="px-4 py-3 text-sm text-gray-700">1 (Fixed)</td>
                  <td className="px-4 py-3 text-sm text-gray-700 font-semibold text-purple-600">4 strategies</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Multiple</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Historical Data</td>
                  <td className="px-4 py-3 text-sm text-gray-700 font-semibold text-purple-600">Yes (PRO)</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Averages</td>
                  <td className="px-4 py-3 text-sm text-gray-700 font-semibold text-purple-600">95+ years</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Various</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Monte Carlo</td>
                  <td className="px-4 py-3 text-sm text-gray-700 font-semibold text-purple-600">Yes (PRO)</td>
                  <td className="px-4 py-3 text-sm text-gray-700">No</td>
                  <td className="px-4 py-3 text-sm text-gray-700 font-semibold text-purple-600">Yes (1,000 trials)</td>
                  <td className="px-4 py-3 text-sm text-gray-700">Some tools</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Phased Retirement</td>
                  <td className="px-4 py-3 text-sm text-gray-700">No</td>
                  <td className="px-4 py-3 text-sm text-gray-700">No</td>
                  <td className="px-4 py-3 text-sm text-gray-700 font-semibold text-purple-600">Yes</td>
                  <td className="px-4 py-3 text-sm text-gray-700">N/A</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-gray-600 mt-3 italic">
            * Accumulation Calculator includes Historical & Monte Carlo analysis in PRO tier only
          </p>
        </div>

        {/* Basic vs Advanced */}
        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">⚠️ Basic vs Advanced: Key Differences</h2>
          
          <div className="space-y-3">
            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">1. Withdrawal Strategies</h3>
              <p className="text-sm text-gray-700"><span className="font-medium">Basic:</span> Fixed dollar amount only</p>
              <p className="text-sm text-gray-700"><span className="font-medium">Advanced:</span> 4 strategies (Fixed, Dynamic SWR, Vanguard Guardrails, Floor & Ceiling)</p>
              <p className="text-sm text-red-600 font-medium">Basic calculator may miss 10-20% higher sustainable income that dynamic strategies could achieve</p>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">2. Historical Data</h3>
              <p className="text-sm text-gray-700"><span className="font-medium">Basic:</span> Single average return rate</p>
              <p className="text-sm text-gray-700"><span className="font-medium">Advanced:</span> 98 years of actual market data (1928-2025)</p>
              <p className="text-sm text-red-600 font-medium">Basic calculator doesn't show worst-case scenarios like the GFC (2008) or 1970s stagflation</p>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">3. Monte Carlo Simulation</h3>
              <p className="text-sm text-gray-700"><span className="font-medium">Basic:</span> Single projection</p>
              <p className="text-sm text-gray-700"><span className="font-medium">Advanced:</span> 1,000 random scenarios with probability analysis</p>
              <p className="text-sm text-red-600 font-medium">Basic calculator can't show success probability (e.g., "85% chance your plan succeeds")</p>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">4. Phased Retirement</h3>
              <p className="text-sm text-gray-700"><span className="font-medium">Basic:</span> Assumes both partners retire at same age</p>
              <p className="text-sm text-gray-700"><span className="font-medium">Advanced:</span> Models partners retiring at different ages with ongoing salary/contributions</p>
              <p className="text-sm text-red-600 font-medium">Basic calculator may underestimate sustainable income by 15-30% for couples with age gaps</p>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">5. Scenario Management</h3>
              <p className="text-sm text-gray-700"><span className="font-medium">Basic:</span> Limited scenario saving</p>
              <p className="text-sm text-gray-700"><span className="font-medium">Advanced:</span> Save unlimited scenarios, load anytime, compare multiple plans</p>
              <p className="text-sm text-red-600 font-medium">Basic calculator makes it harder to test and compare multiple retirement strategies</p>
            </div>
          </div>
        </div>

        {/* Quick Reference */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">📋 Quick Reference</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900 mb-2">"I just want a simple answer"</p>
              <p className="text-blue-700 font-medium">→ Basic Retirement Calculator</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900 mb-2">"I want to optimize my retirement"</p>
              <p className="text-purple-700 font-medium">→ Advanced Retirement Calculator</p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900 mb-2">"I'm still working and saving"</p>
              <p className="text-green-700 font-medium">→ Accumulation Calculator</p>
            </div>

            <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900 mb-2">"I have an SMSF"</p>
              <p className="text-sky-700 font-medium">→ SMSF Suite + Retirement Calculators</p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900 mb-2">"I'm not sure which to use"</p>
              <p className="text-orange-700 font-medium">→ Start with Basic, upgrade if needed</p>
            </div>

            <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900 mb-2">"My partner and I retire at different ages"</p>
              <p className="text-pink-700 font-medium">→ Advanced (Phased Retirement feature)</p>
            </div>
          </div>
        </div>

        {/* Recommended Pathways */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">🎯 Recommended Pathways</h2>
          
          <div className="space-y-4">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Young Professional (Age 25-45)</h3>
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">1. Accumulation</span>
                <span>→</span>
                <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">2. Test contributions</span>
                <span>→</span>
                <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">3. Revisit annually</span>
                <span>→</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">4. At 55+: Basic/Advanced</span>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Mid-Career (Age 45-55)</h3>
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">1. Accumulation</span>
                <span>→</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">2. Basic Retirement</span>
                <span>→</span>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">3. If $500K+: Advanced</span>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Pre-Retiree (Age 55-65)</h3>
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">1. Accumulation (if working)</span>
                <span>→</span>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">2. Advanced Retirement</span>
                <span>→</span>
                <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">3. Test all strategies</span>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Already Retired (Age 65+)</h3>
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">1. Basic (quick check)</span>
                <span>→</span>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">2. If concerned: Advanced</span>
                <span>→</span>
                <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">3. Monitor annually</span>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Couple with Age Gap</h3>
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">1. Accumulation (working partner)</span>
                <span>→</span>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">2. Advanced Retirement</span>
                <span>→</span>
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded">3. Enable Phased Retirement</span>
              </div>
            </div>
          </div>
        </div>

        {/* Important Reminders */}
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">⚠️ Important Reminders</h2>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              <span><strong>Pricing:</strong> Accumulation & Basic are FREE • Advanced & SMSF require PRO subscription</span>
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span><strong>Private</strong> - Data never leaves your device • No account required for FREE tier</span>
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span><strong>Educational purposes only</strong> - Not financial advice</span>
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span><strong>Not a substitute for professional advice</strong> - Consult a licensed financial adviser</span>
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span><strong>Past performance doesn't guarantee future results</strong> - Historical data is for educational purposes</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

